import { chakra, Box as CkBox, Button as CkButton, Img as CkImg } from '@chakra-ui/react'
import { DARK_INPUT_BORDER, DARK_TEXT_PRIMARY } from 'constants/dark-tokens'
import { BaseStyle } from 'types/common'
import chakraShouldForwardProp from 'utils/chakraShouldForwardProp'

/**
 * The lightbox chrome, dark.
 *
 * Its controls used to be white boxes on a near-black backdrop — the brightest
 * thing on screen was the furniture, not the picture. They now sit on the same
 * translucent white fill and hairline every other dark surface uses, so the
 * media is what the eye lands on.
 */
const CONTROL_STYLE = {
  backgroundColor: 'rgba(255, 255, 255, 0.10)',
  color: DARK_TEXT_PRIMARY,
  border: `1px solid ${DARK_INPUT_BORDER}`,
  _hover: { backgroundColor: 'rgba(255, 255, 255, 0.18)' },
  _active: { backgroundColor: 'rgba(255, 255, 255, 0.24)' },
  _focus: { boxShadow: 'none' }
}

export const ModalContainer = chakraShouldForwardProp(CkBox, (props: BaseStyle) => ({
  display: props?.showModal ? 'block' : 'none',
  position: 'fixed',
  zIndex: 99999,
  left: 0,
  top: 0,
  width: '100%',
  height: '100%',
  overflow: 'auto',
  backgroundColor: 'blackAlpha.800'
}))

/**
 * The media, clear of the chrome.
 *
 * A desktop has room to centre it and float the controls over the backdrop
 * beside it. A phone does not: centred, the media ends up under the Download and
 * close buttons at the top and under the paging arrows at the bottom. So there it
 * is parked between the two, and scrolls inside what is left.
 */
export const ModalContent = chakra(CkBox, {
  baseStyle: () => ({
    position: 'absolute',
    left: '50%',
    top: { base: '88px', md: '50%' },
    transform: { base: 'translate(-50%, 0)', md: 'translate(-50%,-50%)' },
    width: '90%',
    maxWidth: 1200,
    maxHeight: { base: 'calc(100dvh - 176px)', md: 'none' },
    overflowY: { base: 'auto', md: 'visible' },
    borderRadius: 4
  })
})

export const CloseButton = chakra(CkButton, {
  baseStyle: () => ({
    ...CONTROL_STYLE,
    position: 'absolute',
    width: '48px',
    height: '48px',
    top: '24px',
    right: '24px',
    borderRadius: 'full',
    fontWeight: 100,
    zIndex: 99999
  })
})

export const PrevButton = chakra(CkButton, {
  baseStyle: () => ({
    ...CONTROL_STYLE,
    cursor: 'pointer',
    position: 'absolute',
    // Beside the media on a desktop, under it on a phone — the pair sits centred
    // at the bottom there rather than on top of what it pages.
    top: { base: 'auto', md: '50%' },
    bottom: { base: '24px', md: 'auto' },
    transform: { base: 'none', md: 'translate(0,-50%)' },
    left: { base: 'calc(50% - 56px)', md: 10 },
    width: 10,
    height: 10,
    borderRadius: 'full',
    fontWeight: 'bold',
    fontSize: 'xl',
    userSelect: 'none'
  })
})

export const NextButton = chakra(CkButton, {
  baseStyle: () => ({
    ...CONTROL_STYLE,
    cursor: 'pointer',
    position: 'absolute',
    top: { base: 'auto', md: '50%' },
    bottom: { base: '24px', md: 'auto' },
    transform: { base: 'none', md: 'translate(0,-50%)' },
    right: { base: 'calc(50% - 56px)', md: 10 },
    width: 10,
    height: 10,
    borderRadius: 'full',
    fontWeight: 'bold',
    fontSize: 'xl',
    userSelect: 'none'
  })
})

export const Slide = chakraShouldForwardProp(CkBox, (props: BaseStyle) => ({
  display: props.display ? 'none' : 'block'
}))

export const ImgBox = chakra(CkImg, {
  baseStyle: () => ({
    width: '100%',
    height: '100%',
    maxHeight: '90vh',
    objectFit: 'contain'
  })
})

export const IndexBox = chakra(CkBox, {
  baseStyle: () => ({
    position: 'absolute',
    width: 40,
    height: 10,
    backgroundColor: 'black',
    borderRadius: 25,
    textAlign: 'center',
    lineHeight: 10,
    color: 'white',
    bottom: '3%',
    left: '50%',
    transform: 'translate(-50%, 0)'
  })
})
